// MACHINERY V2 (Stroje) — Dokumenty upload dialog: dropzone, queued row, the
// rejection strip (M2.5).
//
// Split out of DocsUploadDialogV2.tsx for the 300-line cap. That file owns the
// FILE HANDLING (read, size check, submit); this owns the pixels. Artboard 1004.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { DocumentFile } from "../../../models/machineDocumentsModel";
import FormDropzone from "../../forms/FormDropzone";
import DocNameField from "./docsNameField";
import { FileTile } from "./docsRows";
import { formatDocSize } from "./tabFormat";

export interface QueuedFile {
  key: string;
  name: string;
  file: DocumentFile;
}

/**
 * The Dokumenty well. M2.6 PROMOTED the control itself into the shared kit
 * (`v2/forms/FormDropzone`) so the SR-create attachments field could use the
 * same one instead of a second copy; this wrapper keeps the Dokumenty COPY —
 * including its 11 MB cap line, which stays per-surface (ticket §5 #6: SR
 * create is 10 MB and the two are deliberately not unified).
 */
export const UploadDropzone: React.FC<{
  maxMb: number;
  onFiles: (files: File[]) => void;
}> = ({ maxMb, onFiles }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <FormDropzone
      hint={translate("myMachinery:v2.tabs.docs.uploadHint")}
      limit={translate("myMachinery:v2.tabs.docs.uploadLimit", { mb: maxMb })}
      onFiles={onFiles}
    />
  );
};

export const QueuedFileRow: React.FC<{
  item: QueuedFile;
  showErrors: boolean;
  /** THIS row POST failed on the last attempt (M2.5 review finding R2): it is
   *  still queued, with its edited name, and the retry re-sends exactly it. */
  failed?: boolean;
  onRename: (key: string, name: string) => void;
  onRemove: (key: string) => void;
}> = ({ item, showErrors, failed, onRename, onRemove }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        marginTop: 12,
        padding: "9px 10px",
        borderRadius: 8,
        background: failed ? "color-mix(in oklab, var(--red) 8%, var(--card))" : "var(--card)",
        border: "1px solid " + (failed ? "var(--red)" : "var(--border)"),
        minWidth: 0,
      }}
    >
      <FileTile fileName={item.file.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--ink-2)",
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.file.name}
          >
            {item.file.name}
          </span>
          <span style={{ color: "var(--faint)", flexShrink: 0 }}>
            {formatDocSize(item.file.size)}
          </span>
          {failed && (
            <span style={{ color: "var(--red)", fontWeight: 700, flexShrink: 0 }}>
              {translate("myMachinery:v2.tabs.docs.rowFailed")}
            </span>
          )}
        </div>
        <div style={{ marginTop: 8 }}>
          <DocNameField
            value={item.name}
            showError={showErrors}
            onChange={(v) => onRename(item.key, v)}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onRemove(item.key)}
        aria-label={translate("common:buttons.delete")}
        style={{
          border: 0,
          background: "transparent",
          color: "var(--muted)",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
          padding: 2,
          flexShrink: 0,
        }}
      >
        &times;
      </button>
    </div>
  );
};

/** Amber strip naming the files that did NOT join the queue (artboard 1004). */
export const UploadRejections: React.FC<{ lines: string[] }> = ({ lines }) => {
  if (lines.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginTop: 12,
        padding: "8px 11px",
        borderRadius: 7,
        background: "color-mix(in oklab, var(--amber) 12%, var(--card))",
        border: "1px solid color-mix(in oklab, var(--amber) 40%, var(--border))",
        fontSize: 12,
        color: "var(--ink-2)",
      }}
    >
      <span style={{ color: "var(--amber)", flexShrink: 0 }} aria-hidden>
        &#9888;
      </span>
      <div style={{ minWidth: 0, wordBreak: "break-word" }}>
        {lines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );
};
